/**
 * Extrai cores dominantes de uma imagem (logo).
 * Algoritmo simples por bucketing 5-bit + filtragem de near-white/near-black.
 */
export interface ExtractedPalette {
  primary: string;
  secondary: string;
  accent: string;
  all: { hex: string; count: number }[];
}

const toHex = (r: number, g: number, b: number) =>
  "#" + [r, g, b].map(v => v.toString(16).padStart(2, "0")).join("").toUpperCase();

const luminance = (r: number, g: number, b: number) =>
  (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

const colorDistance = (a: [number,number,number], b: [number,number,number]) =>
  Math.hypot(a[0]-b[0], a[1]-b[1], a[2]-b[2]);

export async function extractPalette(dataUrl: string, opts?: {
  sample?: number;       // 1 a cada N pixels
  bucketBits?: number;   // 5 = bucket de 32 valores
  minDistance?: number;  // distância mínima entre cores escolhidas
}): Promise<ExtractedPalette> {
  const sample = opts?.sample ?? 4;
  const bucketBits = opts?.bucketBits ?? 5;
  const minDist = opts?.minDistance ?? 50;
  const shift = 8 - bucketBits;

  const img = await loadImage(dataUrl);
  const canvas = document.createElement("canvas");
  const maxSide = 200;
  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  canvas.width = Math.max(1, Math.floor(img.width * scale));
  canvas.height = Math.max(1, Math.floor(img.height * scale));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas indisponível.");
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);

  // Histograma
  const buckets = new Map<string, { r: number; g: number; b: number; count: number }>();
  for (let i = 0; i < data.length; i += 4 * sample) {
    const alpha = data[i + 3];
    if (alpha < 100) continue;  // pula transparência
    const r = data[i], g = data[i+1], b = data[i+2];
    const lum = luminance(r, g, b);
    if (lum > 0.95 || lum < 0.05) continue;  // pula quase-branco e quase-preto
    // pula tons de cinza puro
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    if (max - min < 12 && lum > 0.3 && lum < 0.7) continue;

    const key = `${r >> shift}-${g >> shift}-${b >> shift}`;
    const e = buckets.get(key);
    if (e) { e.r += r; e.g += g; e.b += b; e.count++; }
    else buckets.set(key, { r, g, b, count: 1 });
  }

  // Lista ordenada por frequência
  const sorted = [...buckets.values()].map(e => ({
    r: Math.round(e.r / e.count),
    g: Math.round(e.g / e.count),
    b: Math.round(e.b / e.count),
    count: e.count
  })).sort((a, b) => b.count - a.count);

  // Pega top com distância mínima entre si
  const picked: { r: number; g: number; b: number; count: number }[] = [];
  for (const c of sorted) {
    if (picked.length >= 5) break;
    const tuple: [number,number,number] = [c.r, c.g, c.b];
    const tooClose = picked.some(p => colorDistance([p.r,p.g,p.b], tuple) < minDist);
    if (!tooClose) picked.push(c);
  }

  // Fallbacks se a imagem for muito monocromática
  while (picked.length < 3) {
    picked.push({ r: 18, g: 18, b: 20, count: 0 }); // ink preto
  }

  const all = picked.map(c => ({ hex: toHex(c.r, c.g, c.b), count: c.count }));
  return {
    primary:   all[0].hex,
    secondary: all[1].hex,
    accent:    all[2].hex,
    all
  };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Falha ao carregar imagem."));
    img.src = src;
  });
}
